
import React, { useMemo, useRef, useState } from "react";
import applyRules from 'react-jsonschema-form-conditionals';
import Engine from 'json-rules-engine-simplified';
import { IChangeEvent, AjvError } from '@rjsf/core'
import { FormProps } from '@rjsf/core';

import { applyContext, removeContextData } from "./rules.helpers";
import defaultExtraActions from './extraActions'
import { focusOnField, transformErrors } from "./form.helpers"
import _debounce from "lodash/debounce"
import { uiSchemaToStaticErrors } from "./schema.helpers";
import { JSONSchema } from "./RJSF";

export interface IFormWithRules extends FormProps<any> {
    rules?: { [key: string]: any }[]
    extraActions?: { [key: string]: any };
    contextData?: { [key: string]: any }
    contextSchema?: JSONSchema;
    focusOnError?: boolean;
    children?: any
}

export const withRules = (Form: React.FC<any> | React.ComponentClass<any, any>) => {
    return (props: IFormWithRules) => {
        const {
            schema: initialSchema,
            uiSchema: initialUiSchema,
            formData: initialFormData,
            onSubmit,
            onChange,

            rules = [],
            extraActions: moreExtraActions = {},
            contextSchema = {},
            contextData = {},

            focusOnError = true,
            ...otherProps
        } = props;
        console.log({ props })


        const { schema, uiSchema } = applyContext(contextSchema, initialSchema, initialUiSchema)

        const extraActions = { ...defaultExtraActions, ...moreExtraActions };
        const formData = { ...initialFormData, context: contextData, }

        // Uses ref to obtain current formData in case component re-renders and tries to use initial data, configured above
        const formEL: any = useRef();
        const currentFormData = formEL.current?.formData || formData;

        const FormWithConditionals = useMemo(() => {
            return applyRules(schema, uiSchema, rules, Engine, extraActions)(Form)
        }, []); // eslint-disable-line

        const [submitted, setSubmitted] = useState(false)


        const handleSubmit = async (form: IChangeEvent<any> | any, nativeEvent: React.FormEvent<HTMLFormElement>) => {
            !submitted && setSubmitted(true)
            onSubmit && onSubmit({ ...form, formData: removeContextData(form.formData) }, nativeEvent);
        };

        const handleChange = (form: IChangeEvent<any> | any) => {
            if (onChange) {
                onChange({ ...form, formData: removeContextData(form.formData) }); // Context is only meant for schema rules.
            };
        };

        const handleError = (errors: { stack: string }[]) => {
            const firstError = errors[0];

            if (firstError && focusOnError) {
                focusOnField(firstError.stack.split(": ")[0]);
            }

            props.onError && props.onError(errors);
        }

        const handleErrorTransform = (errors: AjvError[]) => {
            let staticErros = uiSchemaToStaticErrors(formEL.current.state.uiSchema);

            if (staticErros) {
                errors = errors.concat(staticErros);
            }

            if (otherProps.transformErrors) {
                errors = otherProps.transformErrors(errors);
            };

            return transformErrors(errors);
        }

        const debounced = _debounce(handleChange, 350)

        return (
            <FormWithConditionals
                ref={formEL}
                {...otherProps}
                onError={handleError}
                onSubmit={handleSubmit}
                schema={schema}
                uiSchema={uiSchema}
                formData={currentFormData}
                transformErrors={handleErrorTransform}
                customFormats={{
                    "date": /^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])$/,
                    "time": /[0-2][0-9]:[0-5][0-9]/
                }}
                //  extraErrors={extraErrors}
                onChange={debounced}
            />
        )
    }
}

